/** Relative folder under My Storage where new editor files are created. */
export const DEFAULT_CONTENT_FOLDER_KEY = 'jarvis.files.defaultContentFolder';
export const DEFAULT_CONTENT_FOLDER_PATH = 'Workspace';
export const DEFAULT_CONTENT_FOLDER_PREFS_CHANGED = 'jarvis:default-content-folder-prefs-changed';

function storageAvailable(): boolean {
  return typeof localStorage !== 'undefined';
}

/**
 * Normalizes a My Storage-relative folder path.
 * Empty / whitespace becomes the factory default (`Workspace`).
 * Rejects `..` segments (falls back to default).
 */
export function normalizeDefaultContentFolderPath(value: unknown): string {
  if (typeof value !== 'string') return DEFAULT_CONTENT_FOLDER_PATH;
  const trimmed = value.replace(/\\/g, '/').trim().replace(/^\/+|\/+$/g, '');
  if (!trimmed) return DEFAULT_CONTENT_FOLDER_PATH;
  const segments = trimmed.split('/').filter(Boolean);
  if (segments.length === 0) return DEFAULT_CONTENT_FOLDER_PATH;
  if (segments.some((segment) => segment === '.' || segment === '..')) {
    return DEFAULT_CONTENT_FOLDER_PATH;
  }
  return segments.join('/');
}

/** Explicit root (empty string). Unlike normalize, empty stays empty. */
export function normalizeContentFolderPathOrRoot(value: unknown): string {
  if (typeof value !== 'string') return DEFAULT_CONTENT_FOLDER_PATH;
  const trimmed = value.replace(/\\/g, '/').trim().replace(/^\/+|\/+$/g, '');
  if (!trimmed) return '';
  const segments = trimmed.split('/').filter(Boolean);
  if (segments.length === 0) return '';
  if (segments.some((segment) => segment === '.' || segment === '..')) {
    return DEFAULT_CONTENT_FOLDER_PATH;
  }
  return segments.join('/');
}

export function readDefaultContentFolderPath(): string {
  if (!storageAvailable()) return DEFAULT_CONTENT_FOLDER_PATH;
  try {
    const raw = localStorage.getItem(DEFAULT_CONTENT_FOLDER_KEY);
    if (raw === null) return DEFAULT_CONTENT_FOLDER_PATH;
    return normalizeContentFolderPathOrRoot(raw);
  } catch {
    return DEFAULT_CONTENT_FOLDER_PATH;
  }
}

export function writeDefaultContentFolderPath(path: string): void {
  if (!storageAvailable()) return;
  const next = normalizeContentFolderPathOrRoot(path);
  try {
    localStorage.setItem(DEFAULT_CONTENT_FOLDER_KEY, next);
    dispatchDefaultContentFolderPrefsChanged();
  } catch {
    /* ignore */
  }
}

export function resetDefaultContentFolderPath(): void {
  writeDefaultContentFolderPath(DEFAULT_CONTENT_FOLDER_PATH);
}

export function formatDefaultContentFolderDisplay(path?: string): string {
  const folder = path === undefined ? readDefaultContentFolderPath() : normalizeContentFolderPathOrRoot(path);
  return folder ? `My Storage / ${folder.replace(/\//g, ' / ')}` : 'My Storage';
}

export function joinContentFolderPath(folderPath: string, fileName: string): string {
  const folder = normalizeContentFolderPathOrRoot(folderPath);
  return folder ? `${folder}/${fileName}` : fileName;
}

export function dispatchDefaultContentFolderPrefsChanged(): void {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(
      new CustomEvent(DEFAULT_CONTENT_FOLDER_PREFS_CHANGED, {
        detail: { path: readDefaultContentFolderPath() }
      })
    );
  } catch {
    /* ignore */
  }
}
